'use client';

import { memo, useEffect, useRef } from 'react';
import {
  useNodesInitialized,
  useReactFlow,
  useStore,
  useViewport,
} from '@xyflow/react';
import type { FitViewOptions, Node } from '@xyflow/react';
import { Box } from '@terra/ui-styled-system';
import { MetaDefault } from '@terra/ui-design-system';
import { formatDuration } from '@terra/ui-formatters';
import type { AttackPathEventNodeData } from './attack-path-event-node.types';
import type { AttackPathEventColumnTick } from './build-attack-path-event-graph.util';
import {
  NODE_HEIGHT,
  NODE_WIDTH,
} from './assessment-attack-graph-layout.constants';
import { isSeedRole } from './attack-path-reveal-theater.util';
import {
  resolveAttackPathAnchoredViewport,
  resolveAttackPathContentBounds,
  type AttackPathViewportNode,
} from './attack-path-anchored-viewport.util';

export const ATTACK_PATH_FIT_VIEW_OPTIONS: FitViewOptions = {
  padding: 0.1,
  duration: 0,
};

export const ATTACK_PATH_COMPACT_FIT_VIEW_OPTIONS: FitViewOptions = {
  padding: 0.02,
  duration: 0,
};

export const ATTACK_PATH_MOTION_CSS = `
@keyframes attack-path-edge-draw {
  from { stroke-dashoffset: 1; }
  to { stroke-dashoffset: 0; }
}
@keyframes attack-path-edge-energy {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -1; }
}
@keyframes attack-path-edge-dash-flow {
  from { stroke-dashoffset: 0; }
  to { stroke-dashoffset: -24; }
}
@keyframes attack-path-node-pulse {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.18); }
}
@keyframes attack-path-node-climax {
  0% { opacity: 0.9; transform: scale(0.98); }
  70% { opacity: 0.35; transform: scale(1.12); }
  100% { opacity: 0; transform: scale(1.2); }
}
@keyframes attack-path-destination-near {
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.22); }
}
@keyframes attack-path-status-dot-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.45; transform: scale(1.35); }
}
@keyframes attack-path-frame-boot {
  0% { opacity: 0; transform: scale(0.96); }
  35% { opacity: 1; transform: scale(1.015); }
  100% { opacity: 1; transform: scale(1); }
}
`;

export const AttackPathMotionStyles = memo(() => (
  <style>{ATTACK_PATH_MOTION_CSS}</style>
));
AttackPathMotionStyles.displayName = 'AttackPathMotionStyles';

type AttackPathViewportAnchor = {
  minFitZoom?: number;
  setViewport: ReturnType<typeof useReactFlow>['setViewport'];
  width: number;
  height: number;
};

const applyAttackPathViewport = ({
  nodes,
  anchor,
  fitView,
  fitViewOptions,
}: {
  nodes: ReadonlyArray<AttackPathViewportNode>;
  anchor: AttackPathViewportAnchor;
  fitView: ReturnType<typeof useReactFlow>['fitView'];
  fitViewOptions: FitViewOptions;
}) => {
  const bounds =
    anchor.minFitZoom === undefined
      ? null
      : resolveAttackPathContentBounds(nodes);

  if (anchor.minFitZoom === undefined || bounds === null) {
    void fitView(fitViewOptions);

    return;
  }

  void anchor.setViewport(
    resolveAttackPathAnchoredViewport({
      bounds,
      containerWidth: anchor.width,
      containerHeight: anchor.height,
      minZoom: anchor.minFitZoom,
    }),
  );
};

export const FitViewOnLayoutKey = ({
  layoutKey,
  fitViewOptions = ATTACK_PATH_FIT_VIEW_OPTIONS,
  minFitZoom,
}: {
  layoutKey: string;
  fitViewOptions?: FitViewOptions;
  minFitZoom?: number;
}) => {
  const { fitView, getNodes, setViewport } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const width = useStore((state) => state.width);
  const height = useStore((state) => state.height);
  const hasSize = width > 0 && height > 0;
  const fitViewRef = useRef(fitView);
  const getNodesRef = useRef(getNodes);
  const fitViewOptionsRef = useRef(fitViewOptions);
  const nodesInitializedRef = useRef(nodesInitialized);
  const hasSizeRef = useRef(hasSize);
  const anchorRef = useRef<{
    minFitZoom?: number;
    setViewport: typeof setViewport;
    width: number;
    height: number;
  }>({ minFitZoom, setViewport, width, height });

  fitViewRef.current = fitView;
  getNodesRef.current = getNodes;
  fitViewOptionsRef.current = fitViewOptions;
  nodesInitializedRef.current = nodesInitialized;
  hasSizeRef.current = hasSize;
  anchorRef.current = { minFitZoom, setViewport, width, height };

  useEffect(() => {
    if (!hasSize || !nodesInitialized) {
      return;
    }

    let cancelled = false;
    const runFit = () => {
      if (cancelled) {
        return;
      }

      const nodes = getNodesRef.current();

      if (nodes.length === 0) {
        return;
      }

      applyAttackPathViewport({
        nodes,
        anchor: anchorRef.current,
        fitView: fitViewRef.current,
        fitViewOptions: fitViewOptionsRef.current,
      });
    };

    let secondFrame = 0;
    let shortTimeout = 0;
    let longTimeout = 0;
    const firstFrame = window.requestAnimationFrame(() => {
      secondFrame = window.requestAnimationFrame(runFit);
      shortTimeout = window.setTimeout(runFit, 50);
      longTimeout = window.setTimeout(runFit, 150);
    });

    return () => {
      cancelled = true;
      window.cancelAnimationFrame(firstFrame);
      window.cancelAnimationFrame(secondFrame);
      window.clearTimeout(shortTimeout);
      window.clearTimeout(longTimeout);
    };
  }, [
    fitViewOptions,
    hasSize,
    height,
    layoutKey,
    minFitZoom,
    nodesInitialized,
    width,
  ]);

  useEffect(() => {
    const runFit = () => {
      if (!hasSizeRef.current || !nodesInitializedRef.current) {
        return;
      }

      const nodes = getNodesRef.current();

      if (nodes.length === 0) {
        return;
      }

      applyAttackPathViewport({
        nodes,
        anchor: anchorRef.current,
        fitView: fitViewRef.current,
        fitViewOptions: fitViewOptionsRef.current,
      });
    };

    window.addEventListener('resize', runFit);

    return () => {
      window.removeEventListener('resize', runFit);
    };
  }, []);

  return null;
};

export const PresentKenBurns = ({
  nodes,
  revealUpToColumn,
  enabled,
}: {
  nodes: Node<AttackPathEventNodeData>[];
  revealUpToColumn?: number;
  enabled?: boolean;
}) => {
  const { setCenter, getZoom } = useReactFlow();
  const lastColumnRef = useRef<number | null>(null);
  const nodesRef = useRef(nodes);
  const setCenterRef = useRef(setCenter);
  const getZoomRef = useRef(getZoom);

  nodesRef.current = nodes;
  setCenterRef.current = setCenter;
  getZoomRef.current = getZoom;

  useEffect(() => {
    if (!enabled || revealUpToColumn === undefined) {
      lastColumnRef.current = null;

      return;
    }

    if (lastColumnRef.current === revealUpToColumn) {
      return;
    }

    lastColumnRef.current = revealUpToColumn;
    const currentNodes = nodesRef.current;
    const frontier = currentNodes.filter(
      (node) =>
        node.data.role !== 'CAMPAIGN_FRAME' &&
        !isSeedRole(node.data.role) &&
        node.data.column === revealUpToColumn,
    );
    const focus =
      frontier[0] ??
      currentNodes.find(
        (node) =>
          node.data.role !== 'CAMPAIGN_FRAME' &&
          node.data.column === revealUpToColumn,
      );

    if (!focus) {
      return;
    }

    setCenterRef.current(
      focus.position.x + NODE_WIDTH / 2,
      focus.position.y + NODE_HEIGHT / 2,
      {
        zoom: Math.min(Math.max(getZoomRef.current(), 0.8), 1.15),
        duration: 700,
      },
    );
  }, [enabled, revealUpToColumn]);

  return null;
};

export const AttackPathTimeAxis = ({
  columns,
}: {
  columns: AttackPathEventColumnTick[];
}) => {
  const { x, zoom } = useViewport();

  if (columns.length === 0) {
    return null;
  }

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      right={0}
      h="28px"
      borderBottomWidth="1px"
      borderColor="border.subtle"
      bg="bg.default/80"
      pointerEvents="none"
      zIndex={4}
      overflow="hidden"
      style={{ backdropFilter: 'blur(2px)' }}
    >
      {columns.map((tick) => {
        const screenX = x + tick.x * zoom;

        return (
          <Box
            key={tick.column}
            position="absolute"
            top={0}
            style={{ left: `${screenX}px` }}
            h="28px"
            borderLeftWidth="1px"
            borderColor="border.subtle"
            pl={1.5}
            display="flex"
            alignItems="center"
          >
            <MetaDefault color="fg.subtle" whiteSpace="nowrap">
              {formatDuration(tick.elapsedSeconds * 1000)}
            </MetaDefault>
          </Box>
        );
      })}
    </Box>
  );
};
